import React, { useEffect, useState, useCallback } from 'react';
import { useAnalystService } from 'hooks/api/agents/analyst';
import { GridRow, GridColumn } from 'app/member/layouts/grid';
import { Button } from 'app/shared/button';
import { MultiSelect } from 'app/shared/select';
import { Spacer } from 'app/layouts/generic';
import DashboardSlidingNav from 'app/member/modules/tenants/tenants-sliding-nav';
import { DomainCard } from '../domain-card';
import { DOMAIN_CARDS } from '../domain-card/config';
import { AnomalyPanel } from '../anomaly-panel';
import { Recommendations } from '../recommendations';
import { AcquisitionReport } from '../acquisition-report';
import { ChannelAdoption } from '../channel-adoption';
import { MonthlyReport } from '../monthly-report';
import styles from './overview.module.css';

const POLL_INTERVAL = 5 * 60 * 1000;
const RUN_SCOPES = [
  { value: 'all', label: 'All domains' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'push', label: 'Push' },
  { value: 'sms', label: 'SMS' },
  { value: 'automation', label: 'Automation' },
  { value: 'people', label: 'People' },
  { value: 'leads', label: 'Leads' },
  { value: 'contacts', label: 'Contacts' },
  { value: 'content', label: 'Content' },
  { value: 'revenue', label: 'Revenue' }
];
const TABS = [
  { path: 'snapshots', name: 'Snapshots' },
  { path: 'acquisition', name: 'Acquisition' },
  { path: 'channels', name: 'Channel Adoption' },
  { path: 'monthly', name: 'Monthly Reports' },
  { path: 'risks', name: 'Risks & Recommendations' }
];

const HealthScoreRing = ({ score }) => {
  const color =
    score >= 80
      ? 'var(--green-primary)'
      : score >= 60
      ? 'var(--orange-primary)'
      : 'var(--red-primary)';
  return (
    <span className={styles.healthScore} style={{ color }}>
      {score ?? '—'}
    </span>
  );
};

export const Overview = () => {
  const { fetchDashboard, fetchAnomalies, triggerRun } = useAnalystService();

  const [dashboard, setDashboard] = useState(null);
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anomaliesLoading, setAnomaliesLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [active_tab, setActiveTab] = useState('snapshots');
  const [run_scope, setRunScope] = useState(RUN_SCOPES[0]);

  const handleRunAnalysis = async () => {
    setRunning(true);
    await triggerRun(run_scope.value);
    setRunning(false);
  };

  const load = useCallback(async () => {
    const [{ dashboard: d }, { anomalies: a }] = await Promise.all([
      fetchDashboard(),
      fetchAnomalies({ query_string: 'active=true' })
    ]);
    setDashboard(d);
    setAnomalies(a || []);
    setLoading(false);
    setAnomaliesLoading(false);
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [load]);

  const domains = dashboard?.domains || {};
  const recommendations = dashboard?.top_recommendations || [];

  return (
    <>
      <GridRow num_of_columns={12} auto_flow={false}>
        <GridColumn span={6} auto_columns={false} />
        <GridColumn span={3} auto_columns={false}>
          <MultiSelect
            options={RUN_SCOPES}
            value={run_scope}
            onChange={setRunScope}
            isSorted={false}
            disabled={running}
          />
        </GridColumn>
        <GridColumn span={3} auto_columns={false}>
          <Button
            text="Run Analysis"
            type="secondary"
            loading={running}
            onClick={handleRunAnalysis}
          />
        </GridColumn>
      </GridRow>

      <Spacer multiple={2} />

      <DashboardSlidingNav nav_items={TABS} activeTab={active_tab} onTabChange={setActiveTab} />

      <Spacer multiple={4} />

      {active_tab === 'snapshots' && (
        <>
          <div className={styles.platformHeader}>
            <div className={styles.healthBlock}>
              <HealthScoreRing score={dashboard?.platform_health_score} />
              <div className={styles.healthMeta}>
                <span className={styles.healthLabel}>Platform health score</span>
                {dashboard?.updated_at && (
                  <span className={styles.updatedAt}>
                    Updated{' '}
                    {new Date(dashboard.updated_at).toLocaleString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.summaryStats}>
              {dashboard?.active_anomaly_count > 0 && (
                <div className={styles.statPill}>
                  <span className={styles.statValue}>{dashboard.active_anomaly_count}</span>
                  <span className={styles.statLabel}>anomalies</span>
                </div>
              )}
              {dashboard?.critical_anomaly_count > 0 && (
                <div className={`${styles.statPill} ${styles.statCritical}`}>
                  <span className={styles.statValue}>{dashboard.critical_anomaly_count}</span>
                  <span className={styles.statLabel}>critical</span>
                </div>
              )}
              {dashboard?.tenants_at_risk > 0 && (
                <div className={`${styles.statPill} ${styles.statWarn}`}>
                  <span className={styles.statValue}>{dashboard.tenants_at_risk}</span>
                  <span className={styles.statLabel}>tenants at risk</span>
                </div>
              )}
            </div>
          </div>

          <Spacer multiple={4} />

          <GridRow num_of_columns={3} auto_flow={false}>
            {DOMAIN_CARDS.map((card) => {
              const domain_data = domains[card.key] || {};
              return (
                <GridColumn key={card.key} span={1} auto_columns={false}>
                  <DomainCard
                    title={card.title}
                    icon={card.icon}
                    headline={card.headline}
                    supporting={card.supporting}
                    metrics={domain_data.metrics || {}}
                    anomaly_count={domain_data.anomaly_count || 0}
                    updated_at={dashboard?.updated_at}
                  />
                </GridColumn>
              );
            })}
          </GridRow>
        </>
      )}

      {active_tab === 'risks' && (
        <GridRow num_of_columns={5} auto_flow={false}>
          <GridColumn span={3} auto_columns={false}>
            <AnomalyPanel anomalies={anomalies} loading={anomaliesLoading} />
          </GridColumn>
          <GridColumn span={2} auto_columns={false}>
            <Recommendations recommendations={recommendations} loading={loading} />
          </GridColumn>
        </GridRow>
      )}

      {active_tab === 'channels' && <ChannelAdoption domains={domains} />}

      {active_tab === 'monthly' && <MonthlyReport />}

      {active_tab === 'acquisition' && (
        <AcquisitionReport leads={domains.leads || {}} people={domains.people || {}} />
      )}
    </>
  );
};
